import api from '@/services/api';
import type {
    DealerSageMappingChange,
    DealerSageMappings,
    DealerSageProductRow,
    SageNominal,
} from '../types/dealerSageMappings.types';
import { isRealDateBetween2000And2099 } from '../schemas/sageNominal.schema';

const WIRE_FIELDS = { code: 'account_code', name: 'account_name' } as const;

const malformed = (detail: string): Error => new Error(`Malformed Sage mappings payload: ${detail}`);

const readRecord = (value: unknown, label: string): Record<string, unknown> => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw malformed(`${label} is not an object`);
    }
    return value as Record<string, unknown>;
};

const readText = (value: unknown, label: string): string => {
    if (typeof value !== 'string' || value === '') throw malformed(`${label} is not a non-empty string`);
    return value;
};

const readNullableText = (value: unknown, label: string): string | null =>
    value === undefined || value === null ? null : readText(value, label);

const readBoolean = (value: unknown, label: string): boolean => {
    if (typeof value !== 'boolean') throw malformed(`${label} is not a boolean`);
    return value;
};

const readNominal = (raw: Record<string, unknown>, label: string): SageNominal | null => {
    const code = readNullableText(raw[WIRE_FIELDS.code], `${label} ${WIRE_FIELDS.code}`);
    const effectiveFrom = readNullableText(raw.effective_from, `${label} effective_from`);
    const name = readNullableText(raw[WIRE_FIELDS.name], `${label} ${WIRE_FIELDS.name}`);
    if (code === null) {
        if (effectiveFrom !== null || name !== null) throw malformed(`${label} has account fields without a code`);
        return null;
    }
    if (effectiveFrom === null || !isRealDateBetween2000And2099(effectiveFrom)) throw malformed(`${label} effective_from is not a date`);
    return { nominalCode: code, nominalName: name, effectiveFrom };
};

const mapProduct = (value: unknown): DealerSageProductRow => {
    const raw = readRecord(value, 'product row');
    return {
        productId: readText(raw.product_id, 'product_id'),
        productName: readNullableText(raw.product_name, 'product_name'),
        entitled: readBoolean(raw.entitled, 'entitled'),
        nominal: readNominal(raw, 'product row'),
    };
};

export const readDealerSageMappings = (data: unknown, requestedDealerId: string): DealerSageMappings => {
    const raw = readRecord(data, 'root');
    const dealerId = readText(raw.dealer_id, 'dealer_id');
    if (dealerId.toLowerCase() !== requestedDealerId.toLowerCase()) throw malformed('dealer_id does not match the request');
    if (!Array.isArray(raw.products)) throw malformed('products is not an array');
    return { dealerId, products: raw.products.map(mapProduct) };
};

export const toWire = ({ productId, nominal }: DealerSageMappingChange) =>
    nominal === null
        ? { product_id: productId, [WIRE_FIELDS.code]: null }
        : {
            product_id: productId,
            [WIRE_FIELDS.code]: nominal.nominalCode,
            [WIRE_FIELDS.name]: nominal.nominalName,
            effective_from: nominal.effectiveFrom,
        };

export const fetchDealerSageMappings = async (dealerId: string): Promise<DealerSageMappings> => {
    const response = await api.get(`/dealers/${dealerId}/sage-mappings`, { suppressGlobalForbiddenToast: true });
    return readDealerSageMappings(response.data?.data, dealerId);
};

export const updateDealerSageMapping = async (dealerId: string, change: DealerSageMappingChange): Promise<void> => {
    await api.put(`/dealers/${dealerId}/sage-mappings`, toWire(change));
};
